<template>
  <section id="about">
    <!-- Mission Section -->
    <div class="mission-section">
      <div class="container">
        <h2 class="section-title">Our Mission</h2>
        <p class="section-subtitle">Building a better internet for everyone</p>

        <div class="mission-content-wrapper">
          <div class="mission-content">
            <div class="mission-text">
              <p class="mission-statement">
                We're building a people-led movement for
                <span class="highlight">a world where everyone has full control over their personal data</span>
                while enjoying the simplest, most convenient internet experience.
              </p>
              <p>
                The Cookie Cutter is our first step toward this vision. It demonstrates how
                <span class="highlight">people-centric data handling</span>
                can actually improve your web experience. It empowers you with more control,
                raises awareness about data collection, and strengthens security rather than compromising it.
              </p>
            </div>
            <div class="mission-image">
              <img
                src="../assets/platformSchema.png"
                alt="Stardust Network infrastructure diagram"
              />
            </div>
          </div>
          <div class="mission-mobile-text">
            <p>
              View the desktop version to read our full mission statement and see our infrastructure diagram.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Values Section -->
    <div class="values-section">
      <div class="container">
        <h2 class="section-title">What We Stand For</h2>
        <p class="section-subtitle">Our core principles guide everything we build</p>

        <div class="values-grid">
          <div class="value-card">
            <div class="value-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </div>
            <h3 class="value-title">Privacy First</h3>
            <p class="value-description">Your data belongs to you. We build tools that protect your privacy by design, not as an afterthought.</p>
          </div>
          <div class="value-card">
            <div class="value-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/>
                <path d="M12 6v6l4 2"/>
              </svg>
            </div>
            <h3 class="value-title">Open Source</h3>
            <p class="value-description">Transparency builds trust. Our code is open for anyone to review, contribute to, and improve.</p>
          </div>
          <div class="value-card">
            <div class="value-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </div>
            <h3 class="value-title">People-Centric</h3>
            <p class="value-description">Technology should serve people, not corporations. We design for users, not advertisers.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Team Section -->
    <div class="team-section">
      <div class="container">
        <h2 class="section-title">Meet the Team</h2>
        <p class="section-subtitle">Privacy advocates, developers, and designers committed to making the internet work better for everyone</p>

        <div class="team-grid">
          <div
            v-for="person in people"
            :key="person.id"
            class="team-card"
          >
            <div class="profile-image-wrapper">
              <img
                :src="require('../assets/people/' + person.profilepic)"
                :alt="person.name + ' profile photo'"
                class="profile-image"
              />
            </div>
            <h3 class="person-name">{{ person.name }}</h3>
            <p class="person-position">{{ person.position }}</p>
            <div class="person-background">
              <span class="background-label">Expertise</span>
              <p>{{ person.background }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Support Section -->
    <div class="support-section">
      <div class="container">
        <div class="support-card">
          <h2 class="section-title support-title">Support Our Mission</h2>
          <p class="support-text">
            The Cookie Cutter is completely free to use. We're funded entirely by donations from people
            like you who believe in a more private, user-friendly internet.
          </p>
          <p class="support-text">
            Want to help? <a href="https://www.reddit.com/r/StardustForum/" target="_blank" rel="noopener noreferrer">Join our community</a>
            to share feedback, report issues, or contribute to development. Every contribution helps us build a better internet for everyone,
            whether it's code, ideas, or donations.
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "AboutUs",
  data() {
    return {
      people: [
        {
          name: "Andrius Matšenas",
          linkedin: "andrius-matsenas",
          position: "CEO & Co-Founder",
          email: "andrius@stardust.today",
          background:
            "Mathematics, Startups, Data Science, Mathematical Modelling, Design",
          profilepic: "Andrius-yellow.png",
          id: 1,
          order: 1,
        },
        {
          name: "Til Jordan",
          linkedin: "til-jordan",
          position: "CTO & Co-Founder",
          email: "til@stardust.today",
          background:
            "Computer Science, Software Development, Cloud Computing, Machine Learning",
          profilepic: "Til-yellow.png",
          id: 2,
          order: 3,
        },
      ],
    };
  },
};
</script>

<style scoped>
/* ============================================
   ABOUT PAGE - SECTIONS
   ============================================ */

#about {
  width: 100%;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* ============================================
   SHARED SECTION STYLES
   ============================================ */

.mission-section,
.values-section,
.team-section,
.support-section {
  padding: 60px 0;
  position: relative;
}

.section-title {
  text-align: center;
  color: #e8ae1b;
  font-size: clamp(32px, 4.5vw, 42px);
  font-weight: 700;
  margin: 0 0 12px 0;
}

.section-subtitle {
  text-align: center;
  color: rgba(254, 254, 254, 0.8);
  font-size: clamp(16px, 1.8vw, 18px);
  margin: 0 0 50px 0;
}

/* ============================================
   MISSION SECTION
   ============================================ */

.mission-content-wrapper {
  background: rgba(40, 54, 60, 0.6);
  border-radius: 16px;
  padding: 35px 30px;
  border: 2px solid rgba(232, 174, 27, 0.2);
  max-width: 1100px;
  margin: 0 auto;
}

.mission-content {
  display: flex;
  gap: 40px;
  align-items: center;
}

.mission-text {
  flex: 2;
}

.mission-statement {
  font-size: clamp(17px, 2.2vw, 20px);
  line-height: 1.7;
  color: #fefefe;
  margin-bottom: 20px;
  font-weight: 500;
}

.mission-text p {
  font-size: clamp(15px, 1.8vw, 17px);
  line-height: 1.7;
  color: rgba(254, 254, 254, 0.8);
  margin-bottom: 16px;
}

.mission-text p:last-child {
  margin-bottom: 0;
}

.mission-image {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 200px;
}

.mission-image img {
  max-width: 220px;
  width: 100%;
  height: auto;
  transition: transform 0.3s ease;
}

.mission-image img:hover {
  transform: scale(1.05);
}

.mission-mobile-text {
  display: none;
  text-align: center;
  padding: 20px 0;
}

.mission-mobile-text p {
  font-size: clamp(14px, 2vw, 16px);
  color: rgba(254, 254, 254, 0.8);
}

/* ============================================
   VALUES SECTION
   ============================================ */

.values-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
  max-width: 1100px;
  margin: 0 auto;
}

.value-card {
  background: rgba(40, 54, 60, 0.6);
  border-radius: 16px;
  padding: 35px 25px;
  border: 2px solid rgba(232, 174, 27, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.value-card:hover {
  transform: translateY(-8px);
  border-color: rgba(232, 174, 27, 0.5);
  background: rgba(40, 54, 60, 0.8);
  box-shadow: 0 12px 32px rgba(232, 174, 27, 0.2);
}

.value-icon {
  width: 70px;
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(232, 174, 27, 0.25) 0%, rgba(232, 174, 27, 0.1) 100%);
  border-radius: 16px;
  margin-bottom: 24px;
  border: 2px solid rgba(232, 174, 27, 0.3);
  transition: all 0.3s ease;
}

.value-card:hover .value-icon {
  background: linear-gradient(135deg, rgba(232, 174, 27, 0.35) 0%, rgba(232, 174, 27, 0.15) 100%);
  border-color: rgba(232, 174, 27, 0.5);
  transform: scale(1.1) rotate(5deg);
}

.value-icon svg {
  width: 36px;
  height: 36px;
  color: #e8ae1b;
}

.value-title {
  font-size: clamp(20px, 2.5vw, 24px);
  font-weight: 600;
  color: #fefefe;
  margin: 0 0 14px 0;
  line-height: 1.3;
}

.value-description {
  font-size: clamp(14px, 1.6vw, 16px);
  color: rgba(254, 254, 254, 0.8);
  line-height: 1.7;
  margin: 0;
}

/* ============================================
   TEAM SECTION
   ============================================ */

.team-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
  max-width: 800px;
  margin: 0 auto;
}

.team-card {
  background: rgba(40, 54, 60, 0.6);
  border-radius: 16px;
  padding: 35px 25px;
  border: 2px solid rgba(232, 174, 27, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.team-card:hover {
  transform: translateY(-8px);
  border-color: rgba(232, 174, 27, 0.5);
  background: rgba(40, 54, 60, 0.8);
  box-shadow: 0 12px 32px rgba(232, 174, 27, 0.2);
}

.profile-image-wrapper {
  width: 100%;
  max-width: 180px;
  margin-bottom: 20px;
}

.profile-image {
  width: 100%;
  height: auto;
  border-radius: 12px;
  transition: transform 0.3s ease;
}

.team-card:hover .profile-image {
  transform: scale(1.05);
}

.person-name {
  font-size: clamp(20px, 2.5vw, 24px);
  font-weight: 600;
  color: #fefefe;
  margin: 0 0 8px 0;
  line-height: 1.3;
}

.person-position {
  font-size: clamp(15px, 1.8vw, 17px);
  font-weight: 500;
  color: #e8ae1b;
  margin: 0 0 16px 0;
}

.person-background {
  width: 100%;
  margin-top: 8px;
  padding-top: 16px;
  border-top: 1px solid rgba(232, 174, 27, 0.2);
}

.background-label {
  font-style: italic;
  font-weight: 500;
  color: rgba(254, 254, 254, 0.6);
  display: block;
  margin-bottom: 8px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.person-background p {
  font-size: clamp(14px, 1.6vw, 16px);
  color: rgba(254, 254, 254, 0.8);
  line-height: 1.6;
  margin: 0;
}

/* ============================================
   SUPPORT SECTION
   ============================================ */

.support-card {
  background: rgba(40, 54, 60, 0.6);
  border-radius: 20px;
  padding: 40px 30px;
  border: 2px solid rgba(232, 174, 27, 0.3);
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
}

.support-title {
  margin-bottom: 24px;
}

.support-text {
  font-size: clamp(15px, 1.8vw, 17px);
  color: rgba(254, 254, 254, 0.8);
  line-height: 1.7;
  margin-bottom: 16px;
}

.support-text:last-child {
  margin-bottom: 0;
}

.support-text a {
  color: #e8ae1b;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.2s ease;
}

.support-text a:hover {
  color: #f3c14c;
  text-decoration: underline;
}

/* ============================================
   RESPONSIVE DESIGN
   ============================================ */

@media (min-width: 900px) {
  .values-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (min-width: 768px) and (max-width: 1100px) {
  .values-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .mission-section,
  .values-section,
  .team-section,
  .support-section {
    padding: 40px 0;
  }

  .section-subtitle {
    margin-bottom: 40px;
  }

  .mission-content-wrapper {
    padding: 30px 20px;
  }

  .values-grid,
  .team-grid {
    gap: 25px;
  }

  .value-card,
  .team-card {
    padding: 30px 20px;
  }

  .support-card {
    padding: 30px 20px;
  }
}

@media (max-width: 700px) {
  .mission-content {
    display: none;
  }

  .mission-mobile-text {
    display: block;
  }
}

@media (max-width: 500px) {
  .mission-section,
  .values-section,
  .team-section,
  .support-section {
    padding: 30px 0;
  }

  .mission-content-wrapper {
    padding: 25px 16px;
  }

  .value-icon {
    width: 60px;
    height: 60px;
  }

  .value-icon svg {
    width: 30px;
    height: 30px;
  }

  .support-card {
    padding: 25px 16px;
  }

  .team-grid,
  .values-grid {
    grid-template-columns: 1fr;
  }
}
</style>
